import "@/App.css";
import { TOPO_A } from "@/assets";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { getVisitorId, getSessionId, markSessionClosed, getLandingUtm } from "@/lib/analytics";
import BetaGate from "@/components/BetaGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import MarketingErrorFallback from "@/components/MarketingErrorFallback";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import ForumModRoute from "@/components/ForumModRoute";
import Landing from "@/pages/Landing";
import Register from "@/pages/Register";
import InviteLanding from "@/pages/InviteLanding";
import Invite from "@/pages/Invite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Settings from "@/pages/Settings";
import SellerPayouts from "@/pages/SellerPayouts";
import VerifyComplete from "@/pages/VerifyComplete";
import BuyCredits from "@/pages/BuyCredits";
import AccountEntry from "@/pages/AccountEntry";
import AuthGoogle from "@/pages/AuthGoogle";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyContactChange from "@/pages/VerifyContactChange";
import DisavowContactChange from "@/pages/DisavowContactChange";
import Feed from "@/pages/Feed";
import Compare from "@/pages/Compare";
import { CompareProvider, CompareBar } from "@/context/CompareContext";
import { InboxUnreadProvider } from "@/context/InboxUnreadContext";
import { CartProvider } from "@/context/CartContext";
import CreateListing from "@/pages/CreateListing";
import ListingDetail from "@/pages/ListingDetail";
import Profile from "@/pages/Profile";
import Inbox from "@/pages/Inbox";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Offers from "@/pages/Offers";
import Pricing from "@/pages/Pricing";
import BusinessWaitlist from "@/pages/BusinessWaitlist";
import FAQ from "@/pages/FAQ";
import Social from "@/pages/Social";
import About from "@/pages/About";
import Partners from "@/pages/Partners";
import MapPage from "@/pages/MapPage";
import Ambassadors from "@/pages/Ambassadors";
import AmbassadorDetail from "@/pages/AmbassadorDetail";
import BrandPage from "@/pages/BrandPage";
import Forum from "@/pages/Forum";
import ForumThread from "@/pages/ForumThread";
import ModDashboard from "@/pages/ModDashboard";
import InTheWild from "@/pages/InTheWild";
import Swag from "@/pages/Swag";
import SwagProduct from "@/pages/SwagProduct";
import SwagCart from "@/pages/SwagCart";
import NotFound from "@/pages/NotFound";
import VerifyLaunch from "@/pages/VerifyLaunch";
import DealerApply from "@/pages/DealerApply";
import BusinessApply from "@/pages/BusinessApply";
import DealerLicenses from "@/pages/DealerLicenses";
import PendingUsers from "@/pages/PendingUsers";
import AdminPortal from "@/pages/AdminPortal";
import AdminModeBar, { ADMIN_MODE_BAR_HEIGHT_PX } from "@/components/AdminModeBar";
import IdleTimeout from "@/components/IdleTimeout";
import NameGate from "@/components/NameGate";
import FoundingInterstitial from "@/components/FoundingInterstitial";
import ReverifyBanner from "@/components/ReverifyBanner";
import CampaignVerifyBanner from "@/components/CampaignVerifyBanner";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSITREP from "@/pages/admin/AdminSITREP";
import AdminAccounts from "@/pages/admin/AdminAccounts";
import AdminContactChanges from "@/pages/admin/AdminContactChanges";
import AdminContentReview from "@/pages/admin/AdminContentReview";
import AdminReverifyCampaign from "@/pages/admin/AdminReverifyCampaign";
import AdminListings from "@/pages/admin/AdminListings";
import AdminLicenses from "@/pages/admin/AdminLicenses";
import AdminCredits from "@/pages/admin/AdminCredits";
import AdminBuyerPasses from "@/pages/admin/AdminBuyerPasses";
import AdminTestData from "@/pages/admin/AdminTestData";
import AdminPromoCodes from "@/pages/admin/AdminPromoCodes";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminFees from "@/pages/admin/AdminFees";
import AdminFeeAudit from "@/pages/admin/AdminFeeAudit";
import AdminPlatformConfig from "@/pages/admin/AdminPlatformConfig";
import AdminTierPermissions from "@/pages/admin/AdminTierPermissions";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminReconciliation from "@/pages/admin/AdminReconciliation";
import AdminAuditEvents from "@/pages/admin/AdminAuditEvents";
import AdminBlocks from "@/pages/admin/AdminBlocks";
import AdminFounding from "@/pages/admin/AdminFounding";
import AdminMembershipCap from "@/pages/admin/AdminMembershipCap";
import AdminAmbassadors from "@/pages/admin/AdminAmbassadors";
import AdminCollaborators from "@/pages/admin/AdminCollaborators";

import AdminSwag from "@/pages/admin/AdminSwag";
import AdminForum from "@/pages/admin/AdminForum";
import AdminPageEditor from "@/pages/admin/AdminPageEditor";
import AdminTeam from "@/pages/admin/AdminTeam";

import Founding from "@/pages/Founding";
import Legal from "@/pages/Legal";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminPartnerApplications from "@/pages/admin/AdminPartnerApplications";
import AdminInvites from "@/pages/AdminInvites";
import AdminShell from "@/components/admin/AdminShell";
import ShipLabelPrint from "@/pages/ShipLabelPrint";
import FeedbackWidget from "@/components/FeedbackModal";
import WelcomeTour from "@/components/WelcomeTour";

// Legacy /messages/:id deep-links → the consolidated inbox thread.
function MessagesRedirect() {
  const { conversationId } = useParams();
  return <Navigate to={`/inbox/${conversationId}`} replace />;
}

// ONE inbox component, wrapped by whichever layout is active. An admin in ADMIN MODE gets the full
// admin shell (sidebar + mode band + INBOX active); everyone else gets the plain member Inbox with
// its own navbar. Member mode is unchanged.
function InboxRoute() {
  const { user } = useAuth();
  // The admin nav rail is part of the admin surface (it even carries its own "Inbox" item), so ANY
  // admin/super-admin gets the shell on /inbox regardless of admin-mode. Deliberately NOT gated on
  // adminMode: that flag lives in sessionStorage, defaults to "user", resets on every fresh login,
  // and only flips to "admin" when you first enter /admin/* — which is why the sidebar kept
  // disappearing whenever an admin opened /inbox directly (URL, bookmark, refresh, post-login).
  const adminLayout = isAdminRole(user?.role);
  if (adminLayout) {
    return <AdminShell><Inbox embedded /></AdminShell>;
  }
  return <Inbox />;
}


function AppRouter() {
  const location = useLocation();
  const { user, adminMode, setAdminMode } = useAuth();
  // Global topo backdrop rides under all PUBLIC + MEMBER pages. Admin keeps its own
  // dense dark shell (texture behind a data table helps nobody), so /admin opts out.
  const isAdmin = location.pathname.startsWith("/admin");
  // /inbox renders inside the admin SHELL while an admin is in admin mode (shared route, one inbox
  // component wrapped by whichever layout is active). It needs the same chrome treatment as /admin:
  // no topo backdrop + reserve space for the 30px mode band.
  // Admins always get admin chrome on /inbox now (shell renders regardless of admin-mode), so the
  // topo backdrop must be off here too. The 30px mode-band padding below stays gated on adminMode.
  const inAdminInbox = location.pathname.startsWith("/inbox") && isAdminRole(user?.role);
  const adminChrome = isAdmin || inAdminInbox;

  // Activate ADMIN MODE only on the TRANSITION *into* the admin portal (spec: activated, never
  // ambient). Keyed on the entry transition — NOT on adminMode — so an explicit "exit admin mode"
  // (which sets USER MODE and navigates away) is never immediately undone by this effect.
  const prevIsAdmin = useRef(false);
  useEffect(() => {
    if (isAdmin && !prevIsAdmin.current && isAdminRole(user?.role) && adminMode !== "impersonate") {
      setAdminMode("admin");
    }
    prevIsAdmin.current = isAdmin;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.role, setAdminMode]);

  useEffect(() => {
    const vid = getVisitorId();
    const ssid = getSessionId();
    api.post("/analytics/beacon", { path: location.pathname, referrer: document.referrer, session_id: ssid, visitor_id: vid, event_type: "pageview", engaged: false, ...getLandingUtm() }).catch(() => {});
    if (localStorage.getItem("os_token") && !document.hidden) {
      api.post("/activity/ping", { session_id: ssid }).catch(() => {});
    }
  }, [location.pathname]);

  // Engagement: fire ONE "engage" beacon on the first genuine interaction per page
  // (click / keypress / scroll). This is what makes a session non-bounced — page load
  // alone never counts.
  useEffect(() => {
    let fired = false;
    const onInteract = () => {
      if (fired) return;
      fired = true;
      api.post("/analytics/beacon", { path: location.pathname, session_id: getSessionId(), visitor_id: getVisitorId(), event_type: "engage", engaged: true }).catch(() => {});
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("scroll", onInteract);
    };
    window.addEventListener("click", onInteract);
    window.addEventListener("keydown", onInteract);
    window.addEventListener("scroll", onInteract, { passive: true });
    return cleanup;
  }, [location.pathname]);

  // Rotate the session on real page unload / tab close (browser-supported signal).
  useEffect(() => {
    const onHide = () => markSessionClosed();
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  // Live activity heartbeat (SITREP + DAU/WAU/MAU) while authenticated.
  useEffect(() => {
    const ping = () => {
      if (!localStorage.getItem("os_token") || document.hidden) return;
      api.post("/activity/ping", { session_id: getSessionId() }).catch(() => {});
    };
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <AdminModeBar />
      <IdleTimeout />
      <ReverifyBanner />
      <CampaignVerifyBanner />
      <NameGate />
      <FoundingInterstitial />
      <div
        className="os-page"
        style={{
          "--os-page-img": `url(${TOPO_A})`,
          // Admin chrome opts OUT of the topo backdrop entirely (its dense dark shell reads better
          // without texture, and translucent admin panels can't hide a backdrop behind them — so any
          // topo would bleed THROUGH every tinted panel). Hide the ::before wash via opacity 0.
          ...(adminChrome ? { "--os-topo-opacity": "0" } : {}),
          // Admin chrome is a FIXED-HEIGHT app shell: pin the wrapper to exactly the viewport and
          // reserve the fixed 30px mode-band's height as top padding INSIDE that height (border-box).
          // The AdminShell inside is h-full (fills this box), so the band space is subtracted rather
          // than ADDED. Without this the 100vh shell + additive band padding overflowed by the band
          // height, letting the whole shell scroll UP under the fixed band — hiding the top bar and
          // the first content row behind it (worst on mobile / with momentum scroll).
          ...(adminChrome
            ? {
                height: "100vh",
                overflow: "hidden",
                ...(isAdminRole(user?.role) && adminMode === "admin"
                  ? { paddingTop: ADMIN_MODE_BAR_HEIGHT_PX }
                  : {}),
              }
            : {}),
        }}
      >
      <Routes>
        <Route path="/" element={<ErrorBoundary fallback={<MarketingErrorFallback />}><Landing /></ErrorBoundary>} />
        <Route path="/join" element={<AccountEntry />} />
        <Route path="/login" element={<Navigate to="/join" replace />} />
        <Route path="/register" element={<Register />} />
        <Route path="/i/:code" element={<InviteLanding />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/google" element={<AuthGoogle />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify" element={<VerifyLaunch />} />
        <Route path="/contact-change/verify" element={<VerifyContactChange />} />
        <Route path="/security/disavow" element={<DisavowContactChange />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/print/ship-label/:orderId" element={<ShipLabelPrint />} />
        <Route path="/business" element={<BusinessWaitlist />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/about" element={<About />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/ambassadors" element={<Ambassadors />} />
        <Route path="/ambassadors/:slug" element={<AmbassadorDetail />} />
        <Route path="/brand/:slug" element={<BrandPage />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/forum/thread/:id" element={<ForumThread />} />
        <Route path="/mod" element={<ForumModRoute><ModDashboard /></ForumModRoute>} />
        <Route path="/in-the-wild" element={<InTheWild />} />
        <Route path="/swag" element={<Swag />} />
        <Route path="/swag/cart" element={<SwagCart />} />
        <Route path="/swag/:slug" element={<SwagProduct />} />
        <Route path="/community" element={<Social />} />
        <Route path="/social" element={<Navigate to="/community" replace />} />
        <Route path="/terms" element={<Legal />} />
        <Route path="/privacy" element={<Legal />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/dealer/apply" element={<DealerApply />} />
        <Route path="/business/apply" element={<ProtectedRoute><BusinessApply /></ProtectedRoute>} />
        <Route path="/dealer/licenses" element={<ProtectedRoute><DealerLicenses /></ProtectedRoute>} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/listing/:username/:seq" element={<ListingDetail />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/u/:username" element={<Profile />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/edit/:id" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/messages" element={<Navigate to="/inbox" replace />} />
        <Route path="/messages/:conversationId" element={<MessagesRedirect />} />
        <Route path="/inbox" element={<ProtectedRoute><InboxRoute /></ProtectedRoute>} />
        <Route path="/inbox/:conversationId" element={<ProtectedRoute><InboxRoute /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/invite" element={<ProtectedRoute><Invite /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/offers" element={<ProtectedRoute><Offers /></ProtectedRoute>} />
        <Route path="/settings/security" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="/settings/profile" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        {/* /account was a fully-built page with zero inbound links (superseded by /profile +
            /settings/profile + /settings/security). Redirect preserves any old bookmark/email link. */}
        <Route path="/account" element={<Navigate to="/profile" replace />} />
        <Route path="/settings/payouts" element={<SellerPayouts />} />
        <Route path="/identity/complete" element={<ProtectedRoute><VerifyComplete /></ProtectedRoute>} />
        <Route path="/verify/complete" element={<ProtectedRoute><VerifyComplete /></ProtectedRoute>} />
        <Route path="/credits" element={<ProtectedRoute><BuyCredits /></ProtectedRoute>} />
        <Route path="/founding" element={<ProtectedRoute><Founding /></ProtectedRoute>} />

        {/* Admin Portal — layout route w/ sidebar shell + <Outlet/> sections. */}
        <Route path="/admin" element={<ProtectedRoute><AdminPortal /></ProtectedRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="team" element={<Navigate to="/inbox" replace />} />
          <Route path="sitrep" element={<AdminSITREP />} />
          <Route path="listings" element={<AdminListings />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="reconciliation" element={<AdminReconciliation />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="moderation" element={<Navigate to="/admin/content-review" replace />} />
          <Route path="blocks" element={<AdminBlocks />} />
          <Route path="feedback" element={<AdminFeedback />} />
          <Route path="partner-applications" element={<AdminPartnerApplications />} />
          <Route path="content-review" element={<AdminContentReview />} />
          <Route path="reverify-campaign" element={<AdminReverifyCampaign />} />
          <Route path="accounts" element={<AdminAccounts />} />
          <Route path="contact-changes" element={<AdminContactChanges />} />
          <Route path="pending-verification" element={<PendingUsers mode="verification" />} />
          <Route path="business-applications" element={<PendingUsers mode="business" />} />
          <Route path="licenses" element={<AdminLicenses />} />
          <Route path="dealers" element={<Navigate to="/admin/pending-verification" replace />} />
          <Route path="rsr-import" element={<Navigate to="/admin/listings" replace />} />
          <Route path="kuiu-import" element={<Navigate to="/admin/listings" replace />} />
          <Route path="credits" element={<AdminCredits />} />
          <Route path="buyer-passes" element={<AdminBuyerPasses />} />
          <Route path="waitlist" element={<AdminInvites />} />
          <Route path="invites" element={<Navigate to="/admin/waitlist" replace />} />
          <Route path="tiers" element={<Navigate to="/admin/platform-config" replace />} />
          <Route path="platform-config" element={<AdminPlatformConfig />} />
          <Route path="tier-permissions" element={<AdminTierPermissions />} />
          <Route path="founding" element={<AdminFounding />} />
          <Route path="membership-cap" element={<AdminMembershipCap />} />
          <Route path="ambassadors" element={<AdminAmbassadors />} />
          <Route path="collaborators" element={<AdminCollaborators />} />
          <Route path="forum" element={<AdminForum />} />
          <Route path="pages/:pageKey" element={<AdminPageEditor />} />
          <Route path="swag" element={<AdminSwag />} />
          <Route path="fees" element={<AdminFees />} />
          <Route path="fee-audit" element={<AdminFeeAudit />} />
          <Route path="promo-codes" element={<AdminPromoCodes />} />
          <Route path="audit-events" element={<AdminAuditEvents />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="test-data" element={<AdminTestData />} />
          <Route path="system-status" element={<Navigate to="/admin/sitrep" replace />} />
        </Route>

        {/* Catch-all — render a real 404 instead of a blank screen for unmatched routes. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </div>
      <CompareBar />
      {/* Feedback modal host — must be mounted everywhere the feedback button appears, INCLUDING the
          admin shell (its toolbar shows the button). It renders nothing until opened. */}
      <FeedbackWidget />
      <WelcomeTour />
    </>
  );
}

function ThemedToaster() {
  const { resolved } = useTheme() || {};
  // Bottom-right keeps toasts clear of the top bar (admin SUPER ADMIN pill / +POST / icons and the
  // public navbar) on every page, which top-right overlapped.
  return <Toaster position="bottom-right" theme={resolved || "dark"} />;
}

function App() {
  return (
    <div className="App grain min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <CompareProvider>
              <InboxUnreadProvider>
                <CartProvider>
                  <BetaGate>
                    <AppRouter />
                  </BetaGate>
                  <ThemedToaster />
                </CartProvider>
              </InboxUnreadProvider>
            </CompareProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
